/**
 * The two pictures the suggest request carries: the sheet, and our own tracing
 * drawn onto it — both small enough to send.
 *
 * ── Why the browser draws them ───────────────────────────────────────────────
 * The outlines exist as pixels nowhere else. Rebuilding the overlay server-side
 * would mean a rasteriser in a Worker and a second definition of what a traced
 * piece looks like — the one thing the review screen has already answered, on a
 * canvas, in front of the operator. So the picture the model is asked about is
 * literally the picture the operator is looking at.
 *
 * The scan is here for a plainer reason: the canvas already holds it decoded.
 * The route used to fetch the stored original back out of R2 and base64 the
 * whole 2–4 MB into the prompt; sending a downscaled copy from the bitmap that
 * is already in memory costs the browser one more `drawImage` and saves an R2
 * read and an order of magnitude of payload per suggestion.
 *
 * ── Why they are small, and why one is JPEG ──────────────────────────────────
 * A 300 DPI A3 sheet is 3500×5000 px. No model reads a hand-written chip better
 * at that size than at 1500, and the difference is megabytes on a factory
 * connection and tokens on every call. Each long side is capped, and if the
 * encoded picture still lands over its ceiling it is re-rendered smaller rather
 * than sent to be refused. The ceilings are the contract's, not local guesses,
 * so the client cannot render to a limit the Worker does not share.
 *
 * The formats differ because the content does. The scan is a photograph, where
 * JPEG's quality knob buys a far smaller file at a size the chips stay legible
 * at; the overlay adds flat red and blue over that same photograph, and its
 * whole job is that those outlines are unambiguous — so it stays PNG, where an
 * edge is an edge and no ringing artefact can be mistaken for a stray mark.
 *
 * Pure geometry helpers here are unit-tested; the drawing itself needs a canvas
 * and is exercised in the browser, like `trace-run.ts` next door.
 */

import {
  base64Bytes,
  MAX_OVERLAY_BYTES,
  MAX_SCAN_IMAGE_BYTES,
} from '../../../worker/api/saya.contract'
import { interiorPoint } from './geometry'

/* ── Sizes ──────────────────────────────────────────────────────────────────── */

/**
 * The long side of the overlay that leaves the browser, and the two smaller
 * ones it falls back to.
 *
 * 1200 is the working size: a chip written with a marker is legible in it, and
 * the outlines are unambiguous. The steps below it exist because PNG has no
 * quality knob — the only way to make a photographic sheet smaller is to make
 * it smaller — and a sheet that will not fit at 640 is one nothing can be read
 * off anyway.
 */
export const OVERLAY_EDGE_STEPS: readonly number[] = [1200, 900, 640]

/**
 * The same ladder for the scan, one rung higher at the top.
 *
 * 1500 rather than 1200 because this picture carries the writing and nothing
 * else does: the overlay's red outlines survive any size the outline is still
 * visible at, while a pencilled "AST 2×1" is the one thing on the sheet that
 * genuinely gets harder to read as the pixels go. It costs almost nothing to
 * give it the extra rung — JPEG at this quality is a few hundred kilobytes at
 * 1500 px — and the step-down below is there for the sheet that somehow is not.
 */
export const SCAN_EDGE_STEPS: readonly number[] = [1500, 1100, 800]

/**
 * The JPEG quality the scan is encoded at.
 *
 * 0,85 is the usual "photographic, no visible artefacts" setting, and the one
 * the live check against the model was run at. Lower starts eating exactly the
 * fine dark-on-light strokes this picture exists to carry.
 */
export const SCAN_JPEG_QUALITY = 0.85

/** Same colours as the review canvas: red outlines. */
const OUTER_STROKE = '#e02a20'
const OUTER_FILL = 'rgba(224, 42, 32, 0.12)'
const BADGE_FILL = 'rgba(255, 255, 255, 0.92)'
const BADGE_TEXT = '#101010'

/**
 * The factor that fits `width × height` inside `maxEdge` on its long side.
 *
 * Never above 1: a small scan is sent at its own size rather than upscaled into
 * bigger blur. Guarded against a zero dimension, which is what an image that
 * failed to decode looks like.
 */
export function overlayScale(width: number, height: number, maxEdge: number): number {
  const longest = Math.max(width, height)
  if (!Number.isFinite(longest) || longest <= 0) return 1
  return Math.min(1, maxEdge / longest)
}

/** True when a rendered overlay is small enough for the route to accept it. */
export function overlayFits(base64: string): boolean {
  return base64Bytes(base64) <= MAX_OVERLAY_BYTES
}

/** The same question for the scan half, against its own ceiling. */
export function scanFits(base64: string): boolean {
  return base64Bytes(base64) <= MAX_SCAN_IMAGE_BYTES
}

/* ── Drawing ────────────────────────────────────────────────────────────────── */

/** One traced piece as the overlay draws it — scan pixels, y down. */
export interface OverlayOutline {
  outer: readonly (readonly [number, number])[]
  /** The number the rail shows beside this piece. */
  label: string
}

function tracePath(
  ctx: CanvasRenderingContext2D,
  ring: readonly (readonly [number, number])[],
  factor: number,
): void {
  ctx.beginPath()
  ring.forEach(([x, y], index) => {
    const px = x * factor
    const py = y * factor
    if (index === 0) ctx.moveTo(px, py)
    else ctx.lineTo(px, py)
  })
  ctx.closePath()
}

/**
 * The sheet alone, drawn to scale on a fresh canvas.
 *
 * Both pictures start here, which is the point: the model is told the second is
 * the first with our outlines on it, and that sentence is only true if they are
 * the same render. Two independent draws would drift the moment either one grew
 * a filter or a margin.
 */
function sheetCanvas(options: {
  image: CanvasImageSource
  pxWidth: number
  pxHeight: number
  factor: number
}): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(options.pxWidth * options.factor))
  canvas.height = Math.max(1, Math.round(options.pxHeight * options.factor))

  // One sentence for both pictures, and deliberately: a null 2D context is the
  // browser refusing to give this tab a canvas at all, which is neither picture's
  // fault and is not made more actionable by naming which of them asked first.
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Tarama görüntüsü hazırlanamadı.')

  ctx.drawImage(options.image, 0, 0, canvas.width, canvas.height)
  return { canvas, ctx }
}

/** A canvas as bare base64 — the `data:` prefix the API does not want, removed. */
function encodeCanvas(canvas: HTMLCanvasElement, type: string, quality?: number): string {
  const encoded = canvas.toDataURL(type, quality)
  return encoded.slice(encoded.indexOf(',') + 1)
}

/**
 * Render at each size in turn, and stop at the first that fits.
 *
 * The last rung is returned even when it is still too big: a 413 with a sentence
 * naming the picture is a better outcome than the screen silently deciding not
 * to ask. Shared by both pictures so "step down until it fits" is one rule with
 * one behaviour at the end of the ladder, not two that agree today.
 */
function renderStepDown(options: {
  steps: readonly number[]
  pxWidth: number
  pxHeight: number
  fits: (base64: string) => boolean
  render: (factor: number) => string
}): string {
  let smallest = ''
  for (const edge of options.steps) {
    smallest = options.render(overlayScale(options.pxWidth, options.pxHeight, edge))
    if (options.fits(smallest)) return smallest
  }
  return smallest
}

/**
 * One rendering pass at one size, as a base64 PNG (no `data:` prefix).
 *
 * The badge is drawn at the same interior point the review canvas puts the
 * number on, so "piece 4" means the same shape to the model, to the operator
 * and to the piece list in the prompt.
 */
function drawOverlay(options: {
  image: CanvasImageSource
  pxWidth: number
  pxHeight: number
  outlines: readonly OverlayOutline[]
  factor: number
}): string {
  const { factor } = options
  const { canvas, ctx } = sheetCanvas(options)

  const stroke = Math.max(1.5, canvas.width / 500)
  const badge = Math.max(11, canvas.width / 55)
  ctx.lineJoin = 'round'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `600 ${Math.round(badge)}px sans-serif`

  for (const outline of options.outlines) {
    if (outline.outer.length < 3) continue

    tracePath(ctx, outline.outer, factor)
    ctx.fillStyle = OUTER_FILL
    ctx.fill('evenodd')
    ctx.strokeStyle = OUTER_STROKE
    ctx.lineWidth = stroke
    ctx.stroke()

    const inside = interiorPoint(
      outline.outer.map(([x, y]): [number, number] => [x * factor, y * factor]),
    )
    if (!inside) continue
    ctx.beginPath()
    ctx.arc(inside[0], inside[1], badge * 0.8, 0, Math.PI * 2)
    ctx.fillStyle = BADGE_FILL
    ctx.fill()
    ctx.fillStyle = BADGE_TEXT
    ctx.fillText(outline.label, inside[0], inside[1])
  }

  return encodeCanvas(canvas, 'image/png')
}

/**
 * The overlay the suggest request carries, as base64 PNG.
 *
 * Renders at the largest size that fits the route's ceiling, stepping down
 * rather than sending a picture that will be refused.
 */
export function renderTraceOverlayPng(options: {
  image: CanvasImageSource
  pxWidth: number
  pxHeight: number
  outlines: readonly OverlayOutline[]
}): string {
  return renderStepDown({
    steps: OVERLAY_EDGE_STEPS,
    pxWidth: options.pxWidth,
    pxHeight: options.pxHeight,
    fits: overlayFits,
    render: (factor) =>
      drawOverlay({
        image: options.image,
        pxWidth: options.pxWidth,
        pxHeight: options.pxHeight,
        outlines: options.outlines,
        factor,
      }),
  })
}

/**
 * The scan the suggest request carries, as base64 JPEG.
 *
 * The same sheet, the same render, no outlines — which is exactly what the
 * prompt promises the model the first picture is. It comes from the bitmap the
 * review canvas already decoded, so this is a resize and an encode rather than a
 * download; the Worker no longer fetches the stored original for this at all.
 */
export function renderScanJpeg(options: {
  image: CanvasImageSource
  pxWidth: number
  pxHeight: number
}): string {
  return renderStepDown({
    steps: SCAN_EDGE_STEPS,
    pxWidth: options.pxWidth,
    pxHeight: options.pxHeight,
    fits: scanFits,
    render: (factor) =>
      encodeCanvas(sheetCanvas({ ...options, factor }).canvas, 'image/jpeg', SCAN_JPEG_QUALITY),
  })
}
